<template>
  <CModal
    :show.sync="show"
    @close="close"

    centered
  >
    <template #header>
        <div>
          <div class="d-flex align-items-center">
            <CIcon name="cipBriefcase01" size="custom-size" width="36" height="36"
                   class="mr-3 icon-circle icon-circle--primary"/>
            <div>
              <h5 class="modal-title">
                <translate translate-context="payout.certification">Company data</translate>
              </h5>
              <p class="m-0">
            <translate translate-context="payout.certification">
             Fill in the required fields to certify your company and enable payments.
            </translate>
          </p>
            </div>
          </div>
        </div>
        <CButtonClose @click="close"/>
      </template>

    <template #default>
      <CForm @submit.prevent="handleSubmit">
        <CCard class="mb-3" style="background-color: #F7FAFF;">
          <CCardBody class="py-3">
            <div class="font-weight-bold mb-2">
              <translate translate-context="payout.certification">Company with VAT</translate>
            </div>
            <div class="custom-control custom-checkbox">
              <input
                type="checkbox"
                class="custom-control-input"
                id="vat-regime-checkbox"
                v-model="form.vat_regime"
                :disabled="!canSubmit"
              />
              <label class="custom-control-label" for="vat-regime-checkbox">
                <translate translate-context="payout.certification">
                  I declare that I am a company subject to the VAT
                </translate>
              </label>
            </div>
          </CCardBody>
        </CCard>

        <!-- Regione di appartenenza -->
        <CCard class="mb-3" style="background-color: #FAFAFA;">
          <CCardBody>
            <div class="font-weight-bold mb-2">
              <translate translate-context="payout.certification">Region</translate>
            </div>
            <div class="d-flex">
              <div class="custom-control custom-radio mr-4">
                <input
                  type="radio"
                  class="custom-control-input"
                  id="region-eu"
                  value="eu"
                  v-model="form.region"
                  :disabled="!canSubmit"
                  @change="onRegionChange"
                />
                <label class="custom-control-label" for="region-eu">EU</label>
              </div>
              <div class="custom-control custom-radio">
                <input
                  type="radio"
                  class="custom-control-input"
                  id="region-extra-eu"
                  value="extra_eu"
                  v-model="form.region"
                  :disabled="!canSubmit"
                  @change="onRegionChange"
                />
                <label class="custom-control-label" for="region-extra-eu">Extra EU</label>
              </div>
            </div>
          </CCardBody>
        </CCard>

        <!-- P.IVA + Verifica (solo EU) -->
        <div v-if="isEu" class="form-group">
          <label class="font-weight-bold">
            <translate translate-context="payout.certification">VAT</translate>
          </label>
          <div class="d-flex align-items-center">
            <div class="position-relative flex-grow-1 mr-2">
              <CInput
                v-model="form.vat_number"
                type="text"
                maxlength="20"
                :placeholder="'IT02118311006'"
                :is-valid="vatValidationState"
                @input="onVatNumberChange"
              />
            </div>
            <CIcon
              v-if="vatValidation === true"
              name="cipCheck"
              class="text-success mr-2 mb-3"
              size="lg"
            />
            <CButton
              color="primary"
              variant="outline"
              class="mb-3"
              :disabled="!canValidateVat || !form.vat_number || isValidatingVat || vatValidation === true"
              @click="handleValidateVat"
            >
              <CSpinner v-if="isValidatingVat" size="sm" class="mr-1" />
              <translate translate-context="payout.certification">Verify</translate>
            </CButton>
          </div>

          <!-- Errore verifica P.IVA -->
          <div
            v-if="vatValidation === false"
            class="text-warning"
            style="font-size: 12px"
          >
              <translate translate-context="payout.certification">
                We were unable to recognize your VAT number. Please make sure you entered it correctly. Otherwise, we ask you to upload a document certifying your company's existence (it will be approved after 48 hours).
              </translate>
          </div>

          <!-- Errore VIES non disponibile -->
          <div
            v-if="vatValidationError"
            class="text-warning"
            style="font-size: 12px"
          >
            {{ vatValidationError }}
          </div>
        </div>

        <!-- Certificazione aziendale - Upload (visibile per Extra EU, o per EU dopo verifica fallita/errore VIES) -->
        <div v-if="showDocumentUpload" class="form-group">
          <label class="font-weight-bold">
            <translate translate-context="payout.certification">Company certification</translate>
          </label>
          <div v-if="!form.document" class="custom-file-upload">
            <input
              type="file"
              ref="fileInput"
              @change="handleFileChange"
              accept=".pdf"
              class="d-none"
            />
            <div
              class="upload-area text-center py-4"
              @click="$refs.fileInput.click()"
              @dragover.prevent="onDragOver"
              @dragleave.prevent="onDragLeave"
              @drop.prevent="onDrop"
              :class="{ 'drag-over': isDragging }"
            >
                <CIcon name="cipUpload02" size="custom-size" :height="30" class="text-primary mb-2 d-block mx-auto" />
              <div>
                <translate translate-context="payout.certification">Drag file here or</translate>
                <a href="#" @click.prevent="$refs.fileInput.click()" class="text-primary">
                  <translate translate-context="payout.certification">click to upload</translate>
                </a>
              </div>
              <small class="text-muted">
                <translate translate-context="payout.invoiceUpload">(Only PDF, max size 10MB)</translate>
              </small>
            </div>
          </div>

          <div v-else class="uploaded-file">
            <CCard>
              <CCardBody class="py-2 px-3 d-flex align-items-center justify-content-between">
                <div class="d-flex align-items-center">
                  <CIcon name="cipDoc" size="lg" class="mr-2 text-primary" />
                  <span>{{ form.document.name }}</span>
                </div>
                <CButton
                  color="danger"
                  size="sm"
                  variant="ghost"
                  @click="removeFile"
                >
                  <CIcon name="cipClose" size="sm" />
                </CButton>
              </CCardBody>
            </CCard>
          </div>
        </div>

        <!-- IBAN -->
        <div class="form-group">
          <label>
            <translate translate-context="payout.certification">IBAN (optional)</translate>
          </label>
          <CInput
            v-model="form.iban"
            type="text"
            maxlength="34"
            :placeholder="'IT60X0542811101000000123456'"
          />
        </div>

        <!-- Info -->
        <PAlert color="info" icon="cipInfo">
          <translate translate-context="payout.certification">
            You can also enter your IBAN later
          </translate>
        </PAlert>
      </CForm>
    </template>

    <template #footer>
      <CButton color="secondary" variant="outline" @click="close">
        <translate translate-context="payout.certification">Cancel</translate>
      </CButton>
      <CButton
        color="primary"
        @click="handleSubmit"
        :disabled="!canSubmit || !isValid || isSubmitting"
      >
        <CSpinner v-if="isSubmitting" size="sm" class="mr-2" />
        <translate translate-context="payout.certification">Confirm</translate>
      </CButton>
    </template>
  </CModal>
</template>

<script>
import { mapActions } from 'vuex'
import PAlert from "@/components/PAlert.vue";

export default {
  name: 'CertificationModal',
  components: {PAlert},
  props: {
    isSubmitting: {
      type: Boolean,
      default: false
    },
    canSubmit: {
      type: Boolean,
      default: true
    },
    canValidateVat: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      show: false,
      isDragging: false,
      isValidatingVat: false,
      vatValidation: null,
      vatValidationError: null,
      form: {
        vat_regime: false,
        region: 'eu',
        document: null,
        iban: '',
        vat_number: ''
      }
    }
  },
  computed: {
    isEu() {
      return this.form.region === 'eu'
    },

    showDocumentUpload() {
      if (!this.isEu) return true
      // EU: mostra upload solo dopo verifica fallita o errore VIES
      return this.vatValidation === false || this.vatValidationError !== null
    },

    vatValidationState() {
      if (this.vatValidation === true) return true
      if (this.vatValidation === false) return false
      return null
    },

    isValid() {
      if (!this.form.vat_regime || !this.form.region) return false

      if (this.isEu) {
        // EU con P.IVA validata → ok senza documento
        if (this.vatValidation === true && this.form.vat_number) return true
        // EU senza P.IVA valida → serve documento
        return !!this.form.document
      }

      // Extra EU → serve sempre documento
      return !!this.form.document
    }
  },
  methods: {
    ...mapActions('payout', ['validateVat']),

    open(certification = null) {
      this.vatValidation = null
      this.vatValidationError = null

      if (certification) {
        this.form.vat_regime = certification.vat_regime || false
        this.form.region = certification.region || 'eu'
        this.form.iban = certification.iban || ''
        this.form.vat_number = certification.vat_number || ''
        this.form.document = null
      } else {
        this.resetForm()
      }
      this.show = true
    },

    close() {
      this.show = false
    },

    resetForm() {
      this.form = {
        vat_regime: false,
        region: 'eu',
        document: null,
        iban: '',
        vat_number: ''
      }
      this.vatValidation = null
      this.vatValidationError = null
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },

    onRegionChange() {
      // Reset stato verifica quando si cambia regione
      this.vatValidation = null
      this.vatValidationError = null
      this.form.document = null
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },

    onVatNumberChange() {
      // Reset stato verifica quando si modifica la P.IVA
      if (this.vatValidation !== null) {
        this.vatValidation = null
        this.vatValidationError = null
      }
    },

    async handleValidateVat() {
      if (!this.form.vat_number) return

      this.vatValidation = null
      this.vatValidationError = null
      this.isValidatingVat = true

      try {
        const result = await this.validateVat(this.form.vat_number)
        this.vatValidation = result.is_valid
      } catch (error) {
        const status = error?.response?.status || error?.status
        if (status === 429) {
          this.vatValidationError = this.$pgettext(
            'payout.certification',
            'Too many verification attempts. Please try again in a few minutes.'
          )
        } else if (status === 503) {
          this.vatValidationError = this.$pgettext(
            'payout.certification',
            'Verification service is currently unavailable. Please try again later or upload a document.'
          )
        } else {
          this.vatValidationError = this.$pgettext(
            'payout.certification',
            'Error during verification. Please try again or upload a document.'
          )
        }
      } finally {
        this.isValidatingVat = false
      }
    },

    handleFileChange(event) {
      const file = event.target.files[0]
      this.processFile(file)
    },

    onDragOver() {
      this.isDragging = true
    },

    onDragLeave() {
      this.isDragging = false
    },

    onDrop(event) {
      this.isDragging = false
      const file = event.dataTransfer.files[0]
      this.processFile(file)
    },

    processFile(file) {
      if (!file) return

      const maxSize = 10 * 1024 * 1024
      if (file.size > maxSize) {
        this.notifyError(this.$pgettext('payout.certification', 'File cannot exceed 10MB'))
        return
      }

      if (file.type !== 'application/pdf') {
        this.notifyError(this.$pgettext('payout.certification', 'Only PDF files are accepted'))
        return
      }

      this.form.document = file
    },

    removeFile() {
      this.form.document = null
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },

    handleSubmit() {
      if (!this.isValid) return

      const formData = new FormData()
      formData.append('vat_regime', this.form.vat_regime)
      formData.append('region', this.form.region)

      if (this.form.document) {
        formData.append('document', this.form.document)
      }
      if (this.form.iban) {
        formData.append('iban', this.form.iban.replace(/\s/g, '').toUpperCase())
      }
      if (this.form.vat_number) {
        formData.append('vat_number', this.form.vat_number.replace(/\s/g, ''))
      }

      this.$emit('submit', formData)
    }
  }
}
</script>
